import React, { memo } from 'react';
import { CalendarCheck, Clock, Pencil, PoundSterling, TrendingUp } from 'lucide-react';
import { MonthlySummary as MonthlySummaryType } from '../types/database';
import { getMonthName } from '../utils/dateUtils';
import { formatCurrency } from '../lib/format';
import { Card, CardBody } from './ui/card';
import { Button } from './ui/button';
import { Stat } from './ui/stat';

interface MonthlySummaryProps {
  summary: MonthlySummaryType;
  onEditRate?: () => void;
}

export const MonthlySummary: React.FC<MonthlySummaryProps> = memo(({ summary, onEditRate }) => {
  const averageHours = summary.daysWorked > 0 ? summary.totalHours / summary.daysWorked : 0;
  const rateSet = summary.hourlyRate > 0;

  return (
    <Card className="relative overflow-hidden">
      {/* Soft brand wash behind the headline figure */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-24 -right-16 size-64 rounded-full bg-brand/20 blur-3xl"
      />

      <CardBody className="relative pt-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium text-fg-subtle">
              Earnings · {getMonthName(new Date(summary.year, summary.month - 1))}
            </p>
            <p className="numeric text-brand-gradient mt-1.5 text-[34px] leading-none font-medium">
              {formatCurrency(summary.totalEarnings)}
            </p>
          </div>
          {onEditRate && (
            <Button variant="secondary" size="sm" onClick={onEditRate}>
              <Pencil />
              Rate
            </Button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2.5">
          <Stat icon={<Clock />} label="Hours" value={summary.totalHours.toFixed(2)} />
          <Stat icon={<CalendarCheck />} label="Days worked" value={summary.daysWorked} />
          <Stat
            icon={<PoundSterling />}
            label="Hourly rate"
            value={rateSet ? formatCurrency(summary.hourlyRate) : <span className="text-fg-subtle">Not set</span>}
          />
          <Stat icon={<TrendingUp />} label="Avg / day" value={`${averageHours.toFixed(2)}h`} />
        </div>
      </CardBody>
    </Card>
  );
});
